import { Component, computed, DestroyRef, inject, signal } from '@angular/core'
import { NgOptimizedImage } from '@angular/common'
import { DialogRef } from '@angular/cdk/dialog'

import { MessagesService } from '@shared/services'
import { Button, Icon } from '@shared/ui'
import { TicketsService } from '../../data/tickets-service/tickets-service'

/**
 * Sube el ticket del gerente, con la cámara o con un archivo. Se cierra con
 * `true` si la carga quedó hecha.
 */
@Component({
  selector: 'upload-ticket-modal',
  imports: [NgOptimizedImage, Button, Icon],
  host: { class: 'card-panel block rounded-[20px] p-4' },
  templateUrl: './upload-ticket-modal.html',
  styles: `
    /* Fila destacada: relleno degradado y filo claro de 1px. El borde va con la
       doble capa de background porque el relleno es opaco. */
    .photo-option {
      border: 1px solid transparent;
      background-image: linear-gradient(180deg, var(--color-blue-sky) 0%, var(--color-blue-navy) 100%), linear-gradient(180deg, var(--color-blue-frost) 0%, var(--color-blue-sky) 100%);
      background-origin: border-box;
      background-clip: padding-box, border-box;
    }
  `
})
export class UploadTicketModal {
  readonly #tickets = inject(TicketsService)
  readonly #messages = inject(MessagesService)

  protected readonly dialogRef = inject<DialogRef<boolean>>(DialogRef)

  readonly #file = signal<File | null>(null)

  /** Vista previa local: el archivo no se ha subido todavía. */
  protected readonly preview = signal('')
  protected readonly error = signal('')
  protected readonly loading = signal(false)

  /** Un fallo temporal se reintenta con el mismo archivo, y el botón lo dice. */
  protected readonly retryable = signal(false)

  protected readonly fileName = computed(() => this.#file()?.name ?? '')
  protected readonly hasFile = computed(() => this.#file() !== null)

  constructor() {
    // La vista previa es un objeto en memoria: hay que soltarlo al cerrar.
    inject(DestroyRef).onDestroy(() => this.#clearPreview())
  }

  protected select(event: Event): void {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    // El input se resetea para poder volver a elegir el mismo archivo.
    input.value = ''
    if (!file) return

    this.error.set('')
    this.retryable.set(false)
    this.#file.set(file)
    this.#clearPreview()
    this.preview.set(URL.createObjectURL(file))
  }

  protected async submit(): Promise<void> {
    const file = this.#file()
    if (!file || this.loading()) return

    this.loading.set(true)
    const result = await this.#tickets.uploadInvoice(file)
    this.loading.set(false)

    if (!result.ok) {
      this.error.set(result.message)
      this.retryable.set(result.retryable)
      return
    }

    this.dialogRef.close(true)
    await this.#messages.success(result.title, result.message)
  }

  #clearPreview(): void {
    const preview = this.preview()
    if (preview) URL.revokeObjectURL(preview)
  }
}
